import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Locate, Mic, Flame, Sparkles } from "lucide-react";
import { PIN_MARKER_COLORS } from "@/lib/canvassingUtils";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { useEnrichment, getScoreColor, type PropertyEnrichment } from "@/hooks/useEnrichment";
import { CanvassingPinList } from "./CanvassingPinList";
import { PropertyDetailSheet } from "./PropertyDetailSheet";
import { HotLeadsDrawer } from "./HotLeadsDrawer";
import { CanvassingTutorial } from "./CanvassingTutorial";
import { QuickOutcomeSheet } from "./QuickOutcomeSheet";
import type { CanvassingPin, CanvassingSession, CanvassingTerritory, PinOutcome } from "@/hooks/useCanvassing";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface CanvassingMapViewProps {
  pins: CanvassingPin[];
  territories: CanvassingTerritory[];
  activeSession: CanvassingSession | null;
  onAddPin: (pin: {
    latitude: number;
    longitude: number;
    address?: string;
    homeowner_name?: string;
    outcome: PinOutcome;
    notes?: string;
    territory_id?: string;
    contact_phone?: string;
  }) => Promise<any>;
  onUpdatePin: (pinId: string, outcome: PinOutcome, notes?: string) => Promise<void>;
  onDeletePin?: (pinId: string) => Promise<void>;
  onAddBreadcrumb?: (lat: number, lng: number) => void;
  onAskBridgette?: () => void;
}

export function CanvassingMapView({
  pins,
  territories,
  activeSession,
  onAddPin,
  onUpdatePin,
  onDeletePin,
  onAddBreadcrumb,
  onAskBridgette,
}: CanvassingMapViewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const enrichmentMarkersRef = useRef<google.maps.Marker[]>([]);
  const breadcrumbPolylineRef = useRef<google.maps.Polyline | null>(null);
  const userMarkerRef = useRef<google.maps.Marker | null>(null);
  const userPulseRef = useRef<google.maps.Circle | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const { isLoaded, loadError } = useGoogleMaps();
  const { enrichments, enrich, fetchExisting, isLoading } = useEnrichment();

  const [mapInitialized, setMapInitialized] = useState(false);
  const [quickSheetOpen, setQuickSheetOpen] = useState(false);
  const [activePin, setActivePin] = useState<CanvassingPin | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeEnrichment, setActiveEnrichment] = useState<PropertyEnrichment | null>(null);
  const [activeAddress, setActiveAddress] = useState<string | undefined>();
  const [activeLocation, setActiveLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [showHotDrawer, setShowHotDrawer] = useState(false);

  // Load existing enrichments on mount
  useEffect(() => {
    fetchExisting();
  }, [fetchExisting]);

  // Geolocation
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setUserLocation({ lat: 41.8781, lng: -87.6298 })
    );

    if (!activeSession) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(newLoc);
        onAddBreadcrumb?.(newLoc.lat, newLoc.lng);
      },
      undefined,
      { enableHighAccuracy: true, maximumAge: 10000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [activeSession?.id, onAddBreadcrumb]);

  // Init map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !userLocation || mapInitialized) return;

    const map = new google.maps.Map(mapRef.current, {
      center: userLocation,
      zoom: 19,
      mapTypeId: "hybrid",
      tilt: 0,
      styles: [
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", stylers: [{ visibility: "off" }] },
      ],
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: { position: google.maps.ControlPosition.TOP_RIGHT },
    });

    geocoderRef.current = new google.maps.Geocoder();

    map.addListener("click", async (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // FIELD-SCOUT PATTERN: instant pin drop, enrichment runs in background.
      // No blocking dialog. User can change outcome via QuickOutcomeSheet anytime.
      if (activeSession) {
        const created = await onAddPin({
          latitude: lat,
          longitude: lng,
          outcome: "not_knocked",
        });
        if (created) {
          setActivePin(created as CanvassingPin);
          setQuickSheetOpen(true);
          // Reverse geocode in background, then update the pin's address
          geocoderRef.current?.geocode({ location: { lat, lng } }).then(r => {
            const addr = r?.results?.[0]?.formatted_address;
            if (addr) setActivePin(p => p ? { ...p, address: addr } : p);
          }).catch(() => {});
        }
      }

      // Always kick off enrichment in background regardless of session state
      let address: string | undefined;
      try {
        const r = await geocoderRef.current?.geocode({ location: { lat, lng } });
        address = r?.results?.[0]?.formatted_address;
      } catch (_) { /* ignore */ }
      setActiveAddress(address);
      setActiveLocation({ lat, lng });

      // If no active session, show the property detail sheet (read-only intel mode)
      if (!activeSession) {
        setActiveEnrichment(null);
        setDetailOpen(true);
        setEnrichLoading(true);
        const result = await enrich(lat, lng, address);
        setActiveEnrichment(result);
        setEnrichLoading(false);
      } else {
        // Session active: enrich silently so it appears as a score marker
        enrich(lat, lng, address);
      }
    });

    googleMapRef.current = map;
    setMapInitialized(true);
  }, [isLoaded, userLocation, mapInitialized, enrich]);

  // User location marker + pulse
  useEffect(() => {
    if (!googleMapRef.current || !userLocation) return;
    if (userMarkerRef.current) {
      userMarkerRef.current.setPosition(userLocation);
      userPulseRef.current?.setCenter(userLocation);
    } else {
      userMarkerRef.current = new google.maps.Marker({
        position: userLocation,
        map: googleMapRef.current,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#3b82f6",
          fillOpacity: 1,
          strokeColor: "#fff",
          strokeWeight: 3,
        },
        title: "You are here",
        zIndex: 999,
      });
      userPulseRef.current = new google.maps.Circle({
        center: userLocation,
        radius: 15,
        fillColor: "#3b82f6",
        fillOpacity: 0.2,
        strokeColor: "#3b82f6",
        strokeOpacity: 0.4,
        strokeWeight: 1,
        map: googleMapRef.current,
      });
    }
  }, [userLocation, mapInitialized]);

  // Render door pins
  useEffect(() => {
    if (!googleMapRef.current || !mapInitialized) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    pins.forEach(pin => {
      const color = PIN_MARKER_COLORS[pin.outcome as PinOutcome] || "#94a3b8";
      const marker = new google.maps.Marker({
        position: { lat: pin.latitude, lng: pin.longitude },
        map: googleMapRef.current!,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 11,
          fillColor: color,
          fillOpacity: 0.95,
          strokeColor: "#fff",
          strokeWeight: 2.5,
        },
        title: pin.address || pin.homeowner_name || "Door",
        zIndex: 50,
      });
      // Tap pin -> open quick outcome sheet (re-classify or delete)
      marker.addListener("click", () => {
        setActivePin(pin);
        setQuickSheetOpen(true);
      });
      markersRef.current.push(marker);
    });
  }, [pins, mapInitialized]);

  // Render enrichment score markers (per-house)
  useEffect(() => {
    if (!googleMapRef.current || !mapInitialized) return;
    enrichmentMarkersRef.current.forEach(m => m.setMap(null));
    enrichmentMarkersRef.current = [];

    enrichments.forEach(e => {
      const color = getScoreColor(e.opportunity_score);
      const marker = new google.maps.Marker({
        position: { lat: e.latitude, lng: e.longitude },
        map: googleMapRef.current!,
        icon: {
          path: "M -10,-10 L 10,-10 L 10,10 L -10,10 Z",
          scale: 1,
          fillColor: color,
          fillOpacity: 0.55,
          strokeColor: color,
          strokeWeight: 2,
        },
        label: {
          text: String(e.opportunity_score),
          color: "#fff",
          fontSize: "11px",
          fontWeight: "700",
        },
        title: `${e.opportunity_score} — ${e.address || "Property"}`,
        zIndex: 30,
      });
      marker.addListener("click", () => {
        setActiveEnrichment(e);
        setActiveAddress(e.address || undefined);
        setActiveLocation({ lat: e.latitude, lng: e.longitude });
        setEnrichLoading(false);
        setDetailOpen(true);
      });
      enrichmentMarkersRef.current.push(marker);
    });
  }, [enrichments, mapInitialized]);

  // Breadcrumbs
  useEffect(() => {
    if (!googleMapRef.current || !mapInitialized) return;
    breadcrumbPolylineRef.current?.setMap(null);
    const crumbs = (activeSession?.gps_breadcrumbs as Array<{ lat: number; lng: number }>) || [];
    if (crumbs.length < 2) return;
    breadcrumbPolylineRef.current = new google.maps.Polyline({
      path: crumbs,
      strokeColor: "#3b82f6",
      strokeOpacity: 0.6,
      strokeWeight: 3,
      map: googleMapRef.current,
    });
  }, [activeSession?.gps_breadcrumbs, mapInitialized]);

  const centerOnUser = () => {
    if (googleMapRef.current && userLocation) {
      googleMapRef.current.panTo(userLocation);
      googleMapRef.current.setZoom(20);
    }
  };

  // Field-scout style: instant-pin at my current GPS location
  const dropPinHere = async () => {
    if (!userLocation || !activeSession) {
      toast.error("Start a session first");
      return;
    }
    const created = await onAddPin({
      latitude: userLocation.lat,
      longitude: userLocation.lng,
      outcome: "not_knocked",
    });
    if (created) {
      setActivePin(created as CanvassingPin);
      setQuickSheetOpen(true);
    }
  };

  const handleQuickPick = async (outcome: PinOutcome) => {
    if (!activePin) return;
    await onUpdatePin(activePin.id, outcome);
    setActivePin(p => p ? { ...p, outcome } : p);
    setQuickSheetOpen(false);
  };

  const handleQuickDelete = async () => {
    if (!activePin || !onDeletePin) return;
    await onDeletePin(activePin.id);
    setQuickSheetOpen(false);
    setActivePin(null);
  };

  const handleKnockFromSheet = async () => {
    setDetailOpen(false);
    if (!activeSession || !activeLocation) return;
    const created = await onAddPin({
      latitude: activeLocation.lat,
      longitude: activeLocation.lng,
      address: activeAddress,
      outcome: "not_knocked",
    });
    if (created) {
      setActivePin(created as CanvassingPin);
      setQuickSheetOpen(true);
    }
  };

  const handleSelectFromDrawer = (e: PropertyEnrichment) => {
    setActiveEnrichment(e);
    setActiveAddress(e.address || undefined);
    setActiveLocation({ lat: e.latitude, lng: e.longitude });
    setEnrichLoading(false);
    setDetailOpen(true);
    googleMapRef.current?.panTo({ lat: e.latitude, lng: e.longitude });
    googleMapRef.current?.setZoom(20);
  };

  const showLoadingForActive = activeLocation
    ? isLoading(activeLocation.lat, activeLocation.lng) || enrichLoading
    : false;

  return (
    <div className="flex h-full">
      <div className="flex-1 relative">
        <div ref={mapRef} className="w-full h-full bg-muted" />

        {/* Tutorial overlay (first visit) */}
        {isLoaded && <CanvassingTutorial />}

        {/* Top-left: Hot leads toggle */}
        <div className="absolute top-3 left-3 z-10">
          <Button
            size="sm"
            variant={showHotDrawer ? "default" : "secondary"}
            onClick={() => setShowHotDrawer(v => !v)}
            className="shadow-lg gap-1.5"
          >
            <Flame className="w-4 h-4" />
            <span className="hidden sm:inline">Hot Houses</span>
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
              {enrichments.filter(e => e.opportunity_score >= 75).length}
            </Badge>
          </Button>
        </div>

        {/* Top-right utilities */}
        <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
          <Button size="icon" variant="secondary" onClick={centerOnUser} className="shadow-lg" title="Center on me">
            <Locate className="w-4 h-4" />
          </Button>
        </div>

        {/* Helper banner */}
        {isLoaded && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 hidden md:flex items-center gap-1.5 bg-background/95 backdrop-blur border border-border rounded-full px-3 py-1.5 text-xs font-medium shadow-md">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            Tap any house — Bridgette enriches it instantly
          </div>
        )}

        {/* Color legend */}
        {isLoaded && (
          <div className="absolute bottom-3 left-3 z-10 bg-background/95 backdrop-blur border border-border rounded-lg p-2 shadow-md text-[10px] space-y-1">
            <div className="font-semibold uppercase tracking-wide text-muted-foreground mb-1">Score</div>
            <LegendDot color="#10b981" label="🔥 Hot 75+" />
            <LegendDot color="#eab308" label="☀️ Warm 50+" />
            <LegendDot color="#f97316" label="❄️ Cold 25+" />
            <LegendDot color="#94a3b8" label="⏭️ Skip" />
          </div>
        )}

        {/* Big "+ Log Door" FAB */}
        {activeSession && (
          <div className="absolute bottom-6 right-6 flex flex-col items-end gap-3 z-20">
            {onAskBridgette && (
              <Button
                size="icon"
                variant="secondary"
                onClick={onAskBridgette}
                className="h-12 w-12 rounded-full shadow-xl"
                title="Ask Bridgette"
              >
                <Mic className="w-5 h-5" />
              </Button>
            )}
            <Button
              onClick={dropPinHere}
              className={cn(
                "h-16 px-6 rounded-full shadow-2xl gap-2 text-base font-semibold",
                "bg-primary hover:bg-primary/90 hover:scale-105 transition-all",
                "ring-4 ring-primary/30"
              )}
            >
              <MapPin className="w-6 h-6" />
              Pin Me Here
            </Button>
          </div>
        )}

        {!activeSession && isLoaded && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-background/95 backdrop-blur-md border border-border rounded-full px-5 py-2.5 text-sm font-medium shadow-xl flex items-center gap-2 z-10">
            <MapPin className="w-4 h-4 text-primary" />
            Start a session to begin logging doors
          </div>
        )}

        {!isLoaded && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <p className="text-muted-foreground">Loading map...</p>
          </div>
        )}

        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <p className="text-destructive">Failed to load Google Maps</p>
          </div>
        )}
      </div>

      {/* Hot leads side drawer */}
      {showHotDrawer && (
        <div className="hidden md:block w-72 border-l border-border bg-background">
          <HotLeadsDrawer enrichments={enrichments} onSelect={handleSelectFromDrawer} />
        </div>
      )}

      {/* Pin list (desktop only when hot drawer is closed) */}
      {!showHotDrawer && (
        <div className="hidden lg:block w-80 border-l border-border overflow-auto">
          <CanvassingPinList pins={pins} onUpdatePin={onUpdatePin} />
        </div>
      )}

      <QuickOutcomeSheet
        open={quickSheetOpen}
        onOpenChange={setQuickSheetOpen}
        address={activePin?.address || undefined}
        currentOutcome={activePin?.outcome}
        onPick={handleQuickPick}
        onDelete={onDeletePin ? handleQuickDelete : undefined}
      />

      <PropertyDetailSheet
        open={detailOpen}
        onOpenChange={setDetailOpen}
        enrichment={activeEnrichment}
        loading={showLoadingForActive}
        address={activeAddress}
        onKnock={handleKnockFromSheet}
        onAskBridgette={onAskBridgette}
      />
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
